import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Icon from './Icon';
import Logo from './Logo';
import { useTheme } from '../hooks/useTheme';
import { contactInfo } from '../mock';

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();

  const handleNavClick = (href) => {
    if (href === '/') {
      navigate('/');
      setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
    } else if (href.startsWith('/#')) {
      const sectionId = href.substring(2);
      if (location.pathname !== '/') {
        navigate('/');
        setTimeout(() => {
          const element = document.getElementById(sectionId);
          if (element) element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      } else {
        const element = document.getElementById(sectionId);
        if (element) element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    } else {
      navigate(href);
      setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
    }
    setMobileMenuOpen(false);
  };

  const navigation = [
    { name: 'Home', href: '/' },
    { name: 'About Us', href: '/about-us' },
    { name: 'Partnership With Us', href: '/partnership/agency' },
    { name: 'Jobs', href: '/jobs' },
    { name: 'Contact', href: '/#contact' },
    { name: 'Track Your Application', href: '/tracker' },
  ];

  const isActive = (href) => {
    if (href.startsWith('/#')) {
      return location.pathname === '/' && location.hash === href.substring(1);
    }
    if (href.startsWith('/partnership')) {
      return location.pathname.startsWith('/partnership');
    }
    return location.pathname === href;
  };

  return (
    <header
      className="sticky top-0 z-50 border-b"
      style={{
        background: 'var(--zg-header-bg)',
        backdropFilter: 'blur(14px)',
        WebkitBackdropFilter: 'blur(14px)',
        borderColor: 'var(--zg-border)',
        boxShadow: '0 1px 24px rgba(45,42,36,.05)',
      }}
    >
      <nav className="mx-auto max-w-7xl px-6">
        <div className="flex h-20 items-center justify-between">
          <Link to="/" className="flex items-center">
            <Logo markSize={36} wordmarkSize={28} floaty ink="var(--zg-ink)" />
          </Link>

          <div className="hidden lg:flex lg:items-center lg:gap-1 ml-12">
            {navigation.map((item) => {
              const active = isActive(item.href);
              return (
                <button
                  key={item.name}
                  onClick={() => handleNavClick(item.href)}
                  className="text-sm font-semibold rounded-full px-4 py-2.5 transition-colors hover:!text-zg-amber"
                  style={{
                    color: active ? '#e08e0b' : 'var(--zg-muted)',
                    background: active ? 'rgba(224,142,11,.12)' : 'transparent',
                  }}
                  onMouseEnter={(e) => {
                    if (!active) e.currentTarget.style.background = 'rgba(224,142,11,.10)';
                  }}
                  onMouseLeave={(e) => {
                    if (!active) e.currentTarget.style.background = 'transparent';
                  }}
                >
                  {item.name}
                </button>
              );
            })}
          </div>

          <div className="flex items-center gap-3.5">
            <button
              type="button"
              onClick={toggleTheme}
              title="Toggle theme"
              className="inline-flex items-center justify-center w-[42px] h-[42px] rounded-full border transition-colors"
              style={{ borderColor: 'var(--zg-border)', color: 'var(--zg-ink)' }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = '#e08e0b';
                e.currentTarget.style.color = '#e08e0b';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'var(--zg-border)';
                e.currentTarget.style.color = 'var(--zg-ink)';
              }}
            >
              <Icon name={theme === 'dark' ? 'sun' : 'moon'} size={18} strokeWidth={1.8} />
            </button>

            <a
              href={`tel:${contactInfo.phone}`}
              className="hidden lg:flex items-center gap-2 text-sm font-semibold text-white rounded-full px-[22px] py-[11px] transition-all"
              style={{ background: '#2d2a24' }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 10px 22px rgba(45,42,36,.28)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'none';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <Icon name="phone" size={16} color="#fff" />
              <span>{contactInfo.phone}</span>
            </a>

            <button
              type="button"
              className="lg:hidden p-2"
              style={{ color: 'var(--zg-ink2)' }}
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              <span style={{ fontSize: 20 }}>{mobileMenuOpen ? '✕' : '☰'}</span>
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <div
            className="lg:hidden py-4 border-t flex flex-col gap-4"
            style={{ borderColor: 'var(--zg-border)', animation: 'fadeInUp .25s ease-out' }}
          >
            <button
              type="button"
              onClick={toggleTheme}
              className="inline-flex items-center gap-2.5 text-base font-semibold"
              style={{ color: 'var(--zg-ink)' }}
            >
              <Icon name={theme === 'dark' ? 'sun' : 'moon'} size={18} />
              <span>{theme === 'dark' ? 'Light mode' : 'Dark mode'}</span>
            </button>
            {navigation.map((item) => {
              const active = isActive(item.href);
              return (
                <button
                  key={item.name}
                  onClick={() => handleNavClick(item.href)}
                  className="text-base font-semibold text-left transition-colors"
                  style={{ color: active ? '#e08e0b' : 'var(--zg-muted)' }}
                >
                  {item.name}
                </button>
              );
            })}
            <div className="pt-4 border-t flex flex-col gap-3" style={{ borderColor: 'var(--zg-border)' }}>
              <a
                href={`tel:${contactInfo.phone}`}
                className="flex items-center justify-center gap-2 text-sm font-medium text-white rounded-md px-4 py-2.5"
                style={{ background: '#2d2a24' }}
              >
                <Icon name="phone" size={16} color="#fff" />
                <span>{contactInfo.phone}</span>
              </a>
              <a
                href={`mailto:${contactInfo.email}`}
                className="flex items-center gap-2 text-sm"
                style={{ color: 'var(--zg-muted)' }}
              >
                <Icon name="mail" size={16} />
                <span>{contactInfo.email}</span>
              </a>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Header;
